import { useEffect, useState, type FormEvent } from 'react'
import { CheckCircle2, Clock, Mail, Send, XCircle } from 'lucide-react'
import { api } from '../../api/client'
import type { AvailableDomain, MailboxRequest } from '../../api/types'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

export function MailboxRequestPage() {
  const { t } = useI18n()
  const [request, setRequest] = useState<MailboxRequest | null>(null)
  const [domains, setDomains] = useState<AvailableDomain[]>([])
  const [prefix, setPrefix] = useState('')
  const [domainId, setDomainId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    Promise.all([
      api.get<MailboxRequest | null>('/user/mailbox-request'),
      api.get<AvailableDomain[]>('/user/available-domains'),
    ]).then(([req, doms]) => {
      setRequest(req)
      setDomains(doms)
      if (doms.length > 0 && !domainId) setDomainId(doms[0].id)
      setLoaded(true)
    }).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'))
  }, [])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const item = await api.post<MailboxRequest>('/user/mailbox-request', { prefix, domainId })
      setRequest(item)
      setSuccess(t('user.requestSubmitted'))
      setTimeout(() => setSuccess(''), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit')
    } finally {
      setSubmitting(false)
    }
  }

  const selectedDomain = domains.find(d => d.id === domainId)

  if (!loaded) return null

  return <>
    <PageHeader title="user.requestMailbox" />

    {error && <p className="mb-4 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</p>}
    {success && <p className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-700">{success}</p>}

    {request?.status === 'pending' && (
      <div className="panel p-6">
        <div className="flex items-center gap-3 mb-3">
          <Clock size={20} className="text-amber-500" />
          <h2 className="text-base font-semibold">{t('user.requestPending')}</h2>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3">
          <p className="text-sm font-medium">{request.prefix}@{request.domainName}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('user.requestPendingDesc')}</p>
        </div>
      </div>
    )}

    {request?.status === 'rejected' && (
      <div className="panel p-6 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <XCircle size={20} className="text-red-500" />
          <h2 className="text-base font-semibold">{t('user.requestRejected')}</h2>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50/50 px-4 py-3">
          <p className="text-sm font-medium">{request.prefix}@{request.domainName}</p>
          {request.adminNote && <p className="text-xs text-muted-foreground mt-1">{t('user.rejectReason')}: {request.adminNote}</p>}
        </div>
      </div>
    )}

    {request?.status === 'approved' && (
      <div className="panel p-6">
        <div className="flex items-center gap-3 mb-3">
          <CheckCircle2 size={20} className="text-emerald-500" />
          <h2 className="text-base font-semibold">{t('user.requestApproved')}</h2>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3">
          <p className="text-sm font-medium">{request.prefix}@{request.domainName}</p>
        </div>
      </div>
    )}

    {(!request || request.status === 'rejected') && (
      <div className="panel p-6">
        <div className="flex items-center gap-3 mb-3">
          <Mail size={20} className="text-primary" />
          <h2 className="text-base font-semibold">{t('user.requestMailboxTitle')}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t('user.requestMailboxDescription')}</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">{t('user.mailboxPrefix')}</label>
            <div className="flex items-center gap-0 mt-1.5">
              <input
                className="field rounded-r-none border-r-0 flex-1"
                value={prefix}
                onChange={e => setPrefix(e.target.value.toLowerCase())}
                placeholder={t('user.prefixPlaceholder')}
                required
              />
              <span className="inline-flex items-center border border-l-0 bg-muted/50 px-3 h-10 text-sm text-muted-foreground rounded-r-lg">@</span>
              <select
                value={domainId}
                onChange={e => setDomainId(e.target.value)}
                className="field rounded-l-none border-l-0 w-auto"
                required
              >
                {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">{t('user.prefixHint')}</p>
          </div>

          {prefix && selectedDomain && (
            <div className="rounded-lg border bg-muted/30 px-4 py-2.5">
              <span className="text-xs text-muted-foreground">{t('user.emailPreview')}: </span>
              <span className="text-sm font-medium">{prefix}@{selectedDomain.name}</span>
            </div>
          )}

          <button disabled={submitting || !prefix || !domainId} className="btn-primary flex items-center gap-2">
            <Send size={14} />
            {submitting ? t('common.loading') : t('user.submitRequest')}
          </button>
        </form>
      </div>
    )}
  </>
}
